import React, { useCallback, useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Collapse,
    Box,
    TableContainer,
    TableHead,
    Typography,
    TableFooter,
} from '@material-ui/core';
import i18n from 'i18n-js';
import { GroupSales, GroupRestaurantSales } from 'api/models';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { Text } from 'components/shared';
import useStyles from '../../styles';
import { FinanceSalesTable, formatServiceValue } from '../finances/components/financeSalesTable';

interface GroupSalesRowProps {
    sale: GroupRestaurantSales;
    currency: string;
}

function GroupSalesRow({ sale, currency }: GroupSalesRowProps) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const isDisabled = !sale.categories || sale.categories.length === 0;

    const handleToggle = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);

    useEffect(() => {
        if (isDisabled) {
            setOpen(false);
        }
    }, [isDisabled]);

    return (
        <>
            <TableRow>
                <TableCell width={24}>
                    <IconButton size="small" disabled={isDisabled} onClick={handleToggle}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell width={'40%'}>
                    <Text text={sale.name} maxLength={50} withoutMore />
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {formatServiceValue(currency, sale.categoriesTotals?.homeDelivery)}
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {formatServiceValue(currency, sale.categoriesTotals?.takeAway)}
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {formatServiceValue(currency, sale.categoriesTotals?.dineIn)}
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {sale.categoriesTotals?.totalItems}
                </TableCell>
                <TableCell width={'20%'} align="right" className={classes.textAccent}>
                    {currency} {sale.categoriesTotals?.totalAmount?.toFixed(2)}
                </TableCell>
            </TableRow>
            <TableRow className={classes.collapseRow}>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={100}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box marginLeft={1} marginRight={1}>
                            <FinanceSalesTable
                                sales={sale}
                                currency={currency}
                                hideHeader
                                hideFooter
                            />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

interface GroupSalesTableProps {
    currency: string;
    sales: GroupSales;
}

export function GroupSalesTable({ currency, sales }: GroupSalesTableProps) {
    const classes = useStyles();
    return (
        <TableContainer className={`${classes.tableContainer} ${classes.tableContainerGroups}`}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell>{i18n.t('common.category')}</TableCell>
                        <TableCell>
                            <Box
                                display={'flex'}
                                alignItems={'end'}
                                justifyContent={'center'}
                                flexDirection={'column'}
                            >
                                <div>{i18n.t('form.homeDelivery')}</div>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    className={classes.textUpper}
                                >
                                    {i18n.t('common.items')} / {currency}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell>
                            <Box
                                display={'flex'}
                                alignItems={'end'}
                                justifyContent={'center'}
                                flexDirection={'column'}
                            >
                                <div>{i18n.t('form.takeAway')}</div>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    className={classes.textUpper}
                                >
                                    {i18n.t('common.items')} / {currency}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell>
                            <Box
                                display={'flex'}
                                alignItems={'end'}
                                justifyContent={'center'}
                                flexDirection={'column'}
                            >
                                <div>{i18n.t('form.dineIn')}</div>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    className={classes.textUpper}
                                >
                                    {i18n.t('common.items')} / {currency}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell align="right">{i18n.t('common.totalItems')}</TableCell>
                        <TableCell align="right" className={classes.textAccent}>
                            {i18n.t('common.totalAmount')}
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {sales.restaurants?.map((sale, i) => (
                        <GroupSalesRow key={i} sale={sale} currency={currency} />
                    ))}
                </TableBody>

                <TableFooter>
                    <TableRow>
                        <TableCell
                            width={'40%'}
                            colSpan={2}
                            className={`${classes.footerText} ${classes.textUpper}`}
                        >
                            {i18n.t('common.total')}
                        </TableCell>
                        <TableCell width={'10%'} align="right" className={classes.footerText}>
                            {formatServiceValue(currency, sales.categoriesTotals.homeDelivery)}
                        </TableCell>
                        <TableCell width={'10%'} align="right" className={classes.footerText}>
                            {formatServiceValue(currency, sales.categoriesTotals.takeAway)}
                        </TableCell>
                        <TableCell width={'10%'} align="right" className={classes.footerText}>
                            {formatServiceValue(currency, sales.categoriesTotals.dineIn)}
                        </TableCell>
                        <TableCell width={'10%'} align="right" className={classes.footerText}>
                            {sales.categoriesTotals.totalItems}
                        </TableCell>
                        <TableCell width={'20%'} align="right" className={classes.footerText}>
                            {currency} {sales.categoriesTotals.totalAmount}
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
}
